import React, { useCallback, useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Chip,
	Tooltip,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import CancelIcon from "@material-ui/icons/Cancel";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import ScheduleMessageModal from "../ScheduleMessageModal";

const useStyles = makeStyles((theme) => ({
	empty: {
		textAlign: "center",
		padding: theme.spacing(3),
		color: "#777",
	},
	body: {
		whiteSpace: "pre-wrap",
		fontSize: 13,
	},
	chipPending: { background: "#fbc02d", color: "#000" },
	chipSent: { background: "#43a047", color: "#fff" },
	chipCanceled: { background: "#bdbdbd", color: "#000" },
	chipFailed: { background: "#e53935", color: "#fff" },
}));

const STATUS_MAP = {
	pending: { label: "Pendente", chip: "chipPending" },
	sent: { label: "Enviada", chip: "chipSent" },
	canceled: { label: "Cancelada", chip: "chipCanceled" },
	failed: { label: "Falhou", chip: "chipFailed" },
};

const formatDate = (iso) => {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleString("pt-BR");
};

const ScheduledMessagesListModal = ({
	open,
	onClose,
	ticketId,
	contactId,
}) => {
	const classes = useStyles();
	const [confirm, confirmDialog] = useConfirm();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [editing, setEditing] = useState(null);
	const [editorOpen, setEditorOpen] = useState(false);

	const load = useCallback(async () => {
		if (!open) return;
		setLoading(true);
		try {
			const params = {};
			if (ticketId) params.ticketId = ticketId;
			else if (contactId) params.contactId = contactId;
			const { data } = await api.get("/scheduled-messages", { params });
			const records = Array.isArray(data?.records)
				? data.records
				: Array.isArray(data)
				? data
				: [];
			setItems(records);
		} catch (err) {
			toast.error("Erro ao listar mensagens agendadas");
		} finally {
			setLoading(false);
		}
	}, [open, ticketId, contactId]);

	useEffect(() => {
		load();
	}, [load]);

	const handleCancel = async (item) => {
		if (!(await confirm({ title: "Cancelar esta mensagem agendada?", message: "Ela não será enviada. Para reagendar é preciso criar de novo." }))) return;
		try {
			await api.post(`/scheduled-messages/${item.id}/cancel`, {});
			toast.success("Mensagem cancelada");
			load();
		} catch (err) {
			toast.error(
				err?.response?.data?.error || "Erro ao cancelar mensagem"
			);
		}
	};

	const handleEdit = (item) => {
		setEditing(item);
		setEditorOpen(true);
	};

	const handleEditClose = () => {
		setEditorOpen(false);
		setEditing(null);
	};

	const handleEditSaved = () => {
		handleEditClose();
		load();
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
				<DialogTitle>
					Mensagens agendadas
					<Tooltip title="Recarregar">
						<IconButton
							size="small"
							style={{ marginLeft: 8 }}
							onClick={load}
							disabled={loading}
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</DialogTitle>
				<DialogContent dividers>
					{loading ? (
						<div className={classes.empty}>
							<CircularProgress size={24} />
						</div>
					) : items.length === 0 ? (
						<div className={classes.empty}>
							<Typography variant="body2">
								Nenhuma mensagem agendada para este cliente.
							</Typography>
						</div>
					) : (
						<List dense>
							{items.map((item) => {
								const meta =
									STATUS_MAP[item.status] || STATUS_MAP.pending;
								const isPending = item.status === "pending";
								return (
									<ListItem key={item.id} divider>
										{confirmDialog}
										<ListItemText
											primary={
												<>
													<Chip
														size="small"
														label={meta.label}
														className={classes[meta.chip]}
														style={{ marginRight: 8 }}
													/>
													<span style={{ fontSize: 12, color: "#555" }}>
														Envio:{" "}
														{formatDate(item.scheduledFor)}
													</span>
												</>
											}
											secondary={
												<span className={classes.body}>
													{item.body}
													{item.errorMessage && (
														<span
															style={{
																color: "#c62828",
																display: "block",
																marginTop: 4,
															}}
														>
															Erro: {item.errorMessage}
														</span>
													)}
												</span>
											}
										/>
										<ListItemSecondaryAction>
											{isPending && (
												<>
													<Tooltip title="Editar">
														<IconButton
															size="small"
															onClick={() => handleEdit(item)}
														>
															<EditIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Cancelar">
														<IconButton
															size="small"
															onClick={() => handleCancel(item)}
														>
															<CancelIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</>
											)}
										</ListItemSecondaryAction>
									</ListItem>
								);
							})}
						</List>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Fechar</Button>
				</DialogActions>
			</Dialog>

			<ScheduleMessageModal
				open={editorOpen}
				onClose={handleEditClose}
				ticketId={ticketId}
				scheduledMessage={editing}
				onSaved={handleEditSaved}
			/>
		</>
	);
};

export default ScheduledMessagesListModal;
